import React, { useState, useRef, useEffect } from 'react';
import styles from './LanguageSelector.module.scss';
import useI18n from '../../../hooks/useI18n';

interface LanguageSelectorProps {
  className?: string;
  compact?: boolean;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ className, compact = false }) => {
  const { changeLanguage, getCurrentLanguage, getAvailableLanguages } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const currentLanguage = getCurrentLanguage();
  const availableLanguages = getAvailableLanguages();

  const currentLang = availableLanguages.find(lang => lang.code === currentLanguage);

  const handleLanguageChange = (languageCode: string) => {
    changeLanguage(languageCode);
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getFlagIcon = (languageCode: string) => {
    switch (languageCode) {
      case 'pt-BR':
        return '🇧🇷';
      case 'pt-PT':
        return '🇵🇹';
      case 'en-US':
        return '🇺🇸';
      case 'fr-FR':
        return '🇫🇷';
      case 'es-ES':
        return '🇪🇸';
      default:
        return '🌐';
    }
  };

  return (
    <div 
      className={`${styles.languageSelector} ${compact ? styles.compact : ''} ${className || ''}`}
      ref={dropdownRef}
    >
      <button
        className={styles.trigger}
        onClick={toggleDropdown}
        aria-label="Selecionar idioma"
        aria-expanded={isOpen}
      >
        <span className={styles.flagIcon}>
          {getFlagIcon(currentLanguage)}
        </span>
        {!compact && (
          <span className={styles.languageName}>
            {currentLang?.name}
          </span>
        )}
        <span className={`${styles.arrow} ${isOpen ? styles.open : ''}`}>
          ▼
        </span>
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          {availableLanguages.map((language) => (
            <button
              key={language.code}
              className={`${styles.option} ${language.code === currentLanguage ? styles.selected : ''}`}
              onClick={() => handleLanguageChange(language.code)}
              title={language.name}
            >
              <span className={styles.flagIcon}>
                {getFlagIcon(language.code)}
              </span>
              {language.code === currentLanguage && (
                <span className={styles.checkmark}>✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
